import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { API, useOrg } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Package, Trash2, Edit2, Search, Upload, Download, Copy } from "lucide-react";

const ItemsPage = () => {
  const { currentOrg } = useOrg();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [search, setSearch] = useState("");
  const [taxes, setTaxes] = useState([]);
  const [importing, setImporting] = useState(false);
  const [categories, setCategories] = useState([]);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    item_type: "product",
    unit_type: "pcs",
    category: "",
    subcategory: "",
    name: "",
    upc: "",
    selling_price: "",
    purchase_price: "",
    taxable: true,
    default_tax_id: "",
    active: true
  });

  const fetchItems = async () => {
    if (!currentOrg) return;
    try {
      const [itemsRes, taxesRes, catsRes] = await Promise.all([
        axios.get(`${API}/items`, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id },
          params: { search }
        }),
        axios.get(`${API}/taxes`, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        }),
        axios.get(`${API}/categories`, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        })
      ]);
      setItems(itemsRes.data);
      setTaxes(taxesRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error("Failed to fetch items:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [currentOrg, search]);

  const resetForm = () => {
    setForm({
      item_type: "product",
      unit_type: "pcs",
      category: "",
      subcategory: "",
      name: "",
      upc: "",
      selling_price: "",
      purchase_price: "",
      taxable: true,
      default_tax_id: "",
      active: true
    });
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setForm({
      item_type: item.item_type,
      unit_type: item.unit_type,
      category: item.category || "",
      subcategory: item.subcategory || "",
      name: item.name,
      upc: item.upc || "",
      selling_price: item.selling_price.toString(),
      purchase_price: item.purchase_price?.toString() || "",
      taxable: item.taxable,
      default_tax_id: item.default_tax_id || "",
      active: item.active
    });
    setDialogOpen(true);
  };

  const handleClone = (item) => {
    setEditingItem(null);
    setForm({
      item_type: item.item_type,
      unit_type: item.unit_type,
      category: item.category || "",
      subcategory: item.subcategory || "",
      name: `${item.name} (Copy)`,
      upc: "",
      selling_price: item.selling_price.toString(),
      purchase_price: item.purchase_price?.toString() || "",
      taxable: item.taxable,
      default_tax_id: item.default_tax_id || "",
      active: item.active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      selling_price: parseFloat(form.selling_price),
      purchase_price: parseFloat(form.purchase_price) || 0,
      default_tax_id: form.default_tax_id || null
    };

    try {
      if (editingItem) {
        await axios.put(`${API}/items/${editingItem.item_id}`, payload, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        });
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/items`, payload, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        });
        toast.success("Item created");
      }
      await fetchItems();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm("Are you sure you want to delete this item?")) return;
    try {
      await axios.delete(`${API}/items/${itemId}`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      toast.success("Item deleted");
      await fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/items/import`, formData, {
        withCredentials: true,
        headers: { 
          'X-Organization-Id': currentOrg.org_id,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`Imported ${response.data.imported} items`);
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} errors occurred`);
      }
      await fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/items/export`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'items_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Items exported");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="items-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-medium text-zinc-900">Items & Services</h1>
          <p className="text-muted-foreground text-sm">Manage your product and service catalog</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            onChange={handleImport}
            className="hidden"
          />
          <Button 
            variant="outline" 
            className="gap-2" 
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="import-items-btn"
          >
            <Upload className="w-4 h-4" /> {importing ? "Importing..." : "Import CSV"}
          </Button>
          <Button variant="outline" className="gap-2" onClick={handleExport} data-testid="export-items-btn">
            <Download className="w-4 h-4" /> Export
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-item-btn">
                <Plus className="w-4 h-4" />
                Add Item
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>{editingItem ? "Edit Item" : "Create Item"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type</Label>
                    <Select value={form.item_type} onValueChange={(v) => setForm({...form, item_type: v})}>
                      <SelectTrigger data-testid="item-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="product">Product</SelectItem>
                        <SelectItem value="service">Service</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Unit Type</Label>
                    <Select value={form.unit_type} onValueChange={(v) => setForm({...form, unit_type: v})}>
                      <SelectTrigger data-testid="unit-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pcs">Pieces</SelectItem>
                        <SelectItem value="box">Box</SelectItem>
                        <SelectItem value="case">Case</SelectItem>
                        <SelectItem value="hourly">Hourly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="name">Item Name *</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setForm({...form, name: e.target.value})}
                      required
                      data-testid="item-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select value={form.category || "no_category"} onValueChange={(v) => setForm({...form, category: v === "no_category" ? "" : v, subcategory: ""})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no_category">No Category</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat.category_id} value={cat.name}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="subcategory">Subcategory</Label>
                    {(() => {
                      const selectedCat = categories.find(c => c.name === form.category);
                      const subs = selectedCat?.subcategories || [];
                      return subs.length > 0 ? (
                        <Select value={form.subcategory || "no_subcategory"} onValueChange={(v) => setForm({...form, subcategory: v === "no_subcategory" ? "" : v})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select subcategory" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="no_subcategory">None</SelectItem>
                            {subs.map((sub) => (
                              <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id="subcategory"
                          value={form.subcategory}
                          onChange={(e) => setForm({...form, subcategory: e.target.value})}
                          placeholder={form.category ? "Type subcategory" : "Select category first"}
                        />
                      );
                    })()}
                  </div>
                  <div>
                    <Label htmlFor="upc">UPC Code</Label>
                    <Input
                      id="upc"
                      value={form.upc}
                      onChange={(e) => setForm({...form, upc: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="selling_price">Selling Price *</Label>
                    <Input
                      id="selling_price"
                      type="number"
                      step="0.01"
                      value={form.selling_price}
                      onChange={(e) => setForm({...form, selling_price: e.target.value})}
                      required
                      data-testid="selling-price-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="purchase_price">Purchase Price</Label>
                    <Input
                      id="purchase_price"
                      type="number"
                      step="0.01"
                      value={form.purchase_price}
                      onChange={(e) => setForm({...form, purchase_price: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Default Tax</Label>
                    <Select value={form.default_tax_id || "none"} onValueChange={(v) => setForm({...form, default_tax_id: v === "none" ? "" : v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select tax rule" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {taxes.map((tax) => (
                          <SelectItem key={tax.tax_id} value={tax.tax_id}>
                            {tax.name} ({tax.tax_type === 'percentage' ? `${tax.rate}%` : `$${tax.rate}`})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={form.taxable}
                        onCheckedChange={(v) => setForm({...form, taxable: v})}
                      />
                      <Label>Taxable</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={form.active}
                        onCheckedChange={(v) => setForm({...form, active: v})}
                      />
                      <Label>Active</Label>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="item-submit-btn">
                    {editingItem ? "Update" : "Create"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search items..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="item-search-input"
        />
      </div>

      {/* Items Table */}
      <div className="bg-white border border-zinc-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center">
            <Package className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-muted-foreground">No items found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Item</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Type</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Category</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Price</th>
                  <th className="text-center py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Status</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.item_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        {item.upc && <p className="text-xs text-muted-foreground">UPC: {item.upc}</p>}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium ${
                        item.item_type === 'product' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                      }`}>
                        {item.item_type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{item.category || '-'}</td>
                    <td className="py-3 px-4 text-right font-medium">${item.selling_price.toFixed(2)}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium ${
                        item.active ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500'
                      }`}>
                        {item.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleClone(item)}
                        title="Clone item"
                        data-testid={`clone-item-${item.item_id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(item)}
                        data-testid={`edit-item-${item.item_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.item_id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemsPage;
